import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import sessionManagementService from '../../../services/sessionManagementService';

const ActiveDevicesList = ({
  sessions,
  loading,
  currentSessionToken,
  onTerminateSession,
  onLogoutAllDevices,
  onRefresh,
}) => {
  const getDeviceIcon = (browser) => {
    const browserLower = browser?.toLowerCase() || '';
    if (browserLower?.includes('chrome')) return 'Chrome';
    if (browserLower?.includes('firefox')) return 'Firefox';
    if (browserLower?.includes('safari')) return 'Safari';
    if (browserLower?.includes('edge')) return 'Globe';
    return 'Monitor';
  };

  const getOSIcon = (os) => {
    const osLower = os?.toLowerCase() || '';
    if (osLower?.includes('windows')) return 'Laptop';
    if (osLower?.includes('mac')) return 'Laptop';
    if (osLower?.includes('linux')) return 'Laptop';
    if (osLower?.includes('android')) return 'Smartphone';
    if (osLower?.includes('ios')) return 'Smartphone';
    return 'Monitor';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Smartphone" size={20} className="text-primary" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-foreground">Active Devices</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Manage all devices where you are currently logged in
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          iconName="RefreshCw"
          disabled={loading}
        >
          Refresh
        </Button>
      </div>

      {/* Sessions List */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 rounded-full bg-muted animate-pulse"></div>
          </div>
        ) : sessions?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="Smartphone" size={48} className="mx-auto mb-3 opacity-50" />
            <p>No active sessions found</p>
          </div>
        ) : (
          sessions?.map((session) => {
            const isCurrentSession = session?.session_token === currentSessionToken;
            return (
              <div
                key={session?.id}
                className={`border rounded-lg p-4 ${
                  isCurrentSession
                    ? 'border-primary bg-primary/5' :'border-border bg-background'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                      <Icon name={getOSIcon(session?.operating_system)} size={20} className="text-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-foreground">
                          {session?.browser || 'Unknown Browser'} on {session?.operating_system || 'Unknown OS'}
                        </h3>
                        {isCurrentSession && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded">
                            Current Device
                          </span>
                        )}
                      </div>
                      <div className="space-y-1">
                        {session?.location && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Icon name="MapPin" size={12} />
                            {session?.location}
                          </p>
                        )}
                        {session?.ip_address && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Icon name="Globe" size={12} />
                            {session?.ip_address}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Icon name="Clock" size={12} />
                          Last active: {sessionManagementService?.formatLastActivity(session?.last_activity_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                  {!isCurrentSession && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onTerminateSession(session?.id)}
                      iconName="X"
                      className="ml-3"
                    >
                      Terminate
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Logout All Devices Button */}
      {sessions?.length > 1 && (
        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-foreground mb-1">Logout All Other Devices</h3>
              <p className="text-xs text-muted-foreground">
                Terminate all active sessions except your current device. This is useful if you suspect unauthorized access.
              </p>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={onLogoutAllDevices}
              iconName="LogOut"
            >
              Logout All
            </Button>
          </div>
        </div>
      )}

      {/* Security Warning */}
      <div className="mt-6 bg-muted/50 rounded-lg p-4 flex items-start gap-3">
        <Icon name="AlertTriangle" size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-muted-foreground">
          <p className="font-medium mb-1">Security Alert</p>
          <p>
            If you see any unfamiliar devices or locations, terminate those sessions immediately and change your password.
            Consider enabling two-factor authentication for additional security.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ActiveDevicesList;